import type { ChavePrimeiroPasso } from "@/lib/services/dashboard-service"

/**
 * O que este navegador guarda do passo a passo do começo.
 *
 * Mora num módulo SEM dependência nenhuma de propósito: quem apaga a leitura no
 * logout é o `auth-service`, e o `trilho.ts` (que busca no servidor) importa o
 * `dashboard-service`, que por sua vez chega no `auth-service` de volta. Com
 * tudo no mesmo arquivo, isso fechava um ciclo de imports.
 *
 * O que se guarda é só um atalho para a tela não piscar: a verdade está no
 * servidor, e "concluído" é um estado final, então quem já saiu do trilho nunca
 * mais espera uma requisição para abrir o painel.
 */

const CHAVE = "wt_trilho_do_comeco"

export type EstadoDoTrilho = {
  obrigatorio: boolean
  etapa: ChavePrimeiroPasso | null
  passos: Record<ChavePrimeiroPasso, boolean>
  aluno: { id: string; nome: string } | null
}

export const TRILHO_LIVRE: EstadoDoTrilho = {
  obrigatorio: false,
  etapa: null,
  passos: { aluno: false, treino: false, marca: false, como_aluno: false },
  aluno: null,
}

/**
 * Com que nome a leitura deste navegador é guardada.
 *
 * **O id NÃO basta**: quem acabou de se cadastrar tem `welltrainer_user` com
 * `id: ""` (o `registrar()` do `auth-service` cai nesse vazio quando a resposta
 * não traz o id), e a home do v2 não chama `refreshUser` para consertar. Com o
 * id sozinho, a tela do trilho e o porteiro usariam chaves diferentes para a
 * mesma pessoa: "Começar a usar o app" apagaria uma e deixaria a outra de pé,
 * e o painel devolveria a pessoa para o trilho que ela acabou de terminar.
 *
 * O e-mail é reserva porque isto é só uma chave local: ela separa contas no
 * mesmo aparelho, e não autentica nada.
 */
export function chaveDaConta(
  usuario: { id?: string | null; email?: string | null } | null | undefined,
): string | undefined {
  return usuario?.id || usuario?.email || undefined
}

export function trilhoGuardado(usuarioId?: string): EstadoDoTrilho | null {
  if (typeof window === "undefined" || !usuarioId) return null
  try {
    const cru = localStorage.getItem(`${CHAVE}:${usuarioId}`)
    if (!cru) return null
    return JSON.parse(cru) as EstadoDoTrilho
  } catch {
    return null
  }
}

export function guardarTrilho(usuarioId: string | undefined, estado: EstadoDoTrilho) {
  if (typeof window === "undefined" || !usuarioId) return
  try {
    localStorage.setItem(`${CHAVE}:${usuarioId}`, JSON.stringify(estado))
  } catch {
    /* Aba anônima com armazenamento bloqueado: o trilho continua funcionando,
       só perde o atalho de não piscar. */
  }
}

/** Sem o id (o logout), tira a de todas as contas que passaram por aqui. */
export function esquecerTrilho(usuarioId?: string) {
  if (typeof window === "undefined") return
  try {
    if (usuarioId) {
      localStorage.removeItem(`${CHAVE}:${usuarioId}`)
      return
    }
    Object.keys(localStorage)
      .filter((k) => k.startsWith(`${CHAVE}:`))
      .forEach((k) => localStorage.removeItem(k))
  } catch {
    /* idem */
  }
}
